import { Component } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import AuthGate from './auth/AuthGate.jsx'
import AppShell from './layout/AppShell.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Customers from './pages/Customers.jsx'
import CustomerDetail from './pages/CustomerDetail.jsx'
import CustomerNew from './pages/CustomerNew.jsx'
import Invoices from './pages/Invoices.jsx'
import Billing from './pages/Billing.jsx'
import Settings from './pages/Settings.jsx'
import FailedPayments from './pages/FailedPayments.jsx'
import Overdue from './pages/Overdue.jsx'
import PayPage from './payment/PayPage.jsx'

// Top-level routes split into PUBLIC (no auth gate) and ADMIN (gated).
// Public surfaces:
//   /pay            — YesNet-branded invoice payment page; the ONLY
//                     customer-facing payment-setup surface. The
//                     FirstTimeChooser inside PayPage renders the three
//                     payment-method choices (Auto Draft / Bank on File /
//                     Card on File) when the customer has no saved PM.
export default function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/pay/*" element={<PayPage />} />
        <Route path="*" element={<AdminApp />} />
      </Routes>
    </ErrorBoundary>
  )
}

function AdminApp() {
  return (
    <AuthGate>
      <Routes>
        <Route element={<AppShell />}>
          <Route index element={<Dashboard />} />
          {/* Four strictly-partitioned Customers tabs. (customerType,
              billingCycle) uniquely places every customer. AL never
              appears in YN, and 1st never appears in 15th. */}
          <Route path="customers" element={<Navigate to="/customers/al-1st" replace />} />
          <Route path="customers/al-1st"  element={<Customers customerType="program" billingCycle="1st"  />} />
          <Route path="customers/al-15th" element={<Customers customerType="program" billingCycle="15th" />} />
          <Route path="customers/yn-1st"  element={<Customers customerType="invoice" billingCycle="1st"  />} />
          <Route path="customers/yn-15th" element={<Customers customerType="invoice" billingCycle="15th" />} />
          {/* Additive 5th tab — custom-cycle invoice customers (own send/due days). */}
          <Route path="customers/yn-custom" element={<Customers customerType="invoice" billingCycle="custom" />} />
          {/* Back-compat redirects so any old in-app or bookmarked links
              land on a sensible new tab instead of 404'ing. */}
          <Route path="customers/al-auto"    element={<Navigate to="/customers/al-1st"  replace />} />
          <Route path="customers/al-invoice" element={<Navigate to="/customers/al-1st"  replace />} />
          <Route path="customers/qb-auto"    element={<Navigate to="/customers/yn-1st"  replace />} />
          <Route path="customers/qb-invoice" element={<Navigate to="/customers/yn-1st"  replace />} />
          <Route path="customers/al"         element={<Navigate to="/customers/al-1st"  replace />} />
          <Route path="customers/qb"         element={<Navigate to="/customers/yn-1st"  replace />} />
          <Route path="customers/autodraft"  element={<Navigate to="/customers/al-1st"  replace />} />
          <Route path="customers/invoice"    element={<Navigate to="/customers/yn-15th" replace />} />
          <Route path="customers/new" element={<CustomerNew />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="failed-payments" element={<FailedPayments />} />
          <Route path="overdue" element={<Overdue />} />
          <Route path="billing" element={<Billing />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthGate>
  )
}

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[wifibilling] render crash:', error, info)
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-brand-green text-white p-6">
          <div className="bg-white text-gray-900 rounded-2xl p-6 max-w-md w-full text-center shadow-xl">
            <div className="text-4xl mb-2">⚠️</div>
            <h2 className="font-extrabold text-lg">Something went wrong</h2>
            <pre className="text-left text-[11px] text-gray-700 bg-gray-50 rounded p-2 mt-3 overflow-auto max-h-48">
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
            <button className="btn-primary mt-4" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
